/**
 * GlyphWarmingManager.ts
 * 
 * Manages glyph path warming for visible content.
 * Extracts glyph IDs from snapshot lines and requests uncached glyph paths.
 * 
 * REFACTORING_ROADMAP.md Step 11 (partial)
 * Extracted from EdgeDocsCanvas.V3.tsx renderText function
 * 
 * @module GlyphWarmingManager
 */

import { filterUncachedGlyphs } from './GlyphCacheManager';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Minimal glyph interface from snapshot
 */
interface SnapshotGlyph {
  id?: number;
  [key: string]: unknown;
}

/**
 * Minimal run interface from snapshot
 */
interface SnapshotRun {
  glyphs?: SnapshotGlyph[];
  font_fingerprint?: {
    font_uid?: string;
    family?: string;
    postscript_name?: string;
  };
  font_size_fp?: number;
  [key: string]: unknown;
}

/**
 * Minimal line interface from snapshot
 */
interface SnapshotLine {
  runs?: SnapshotRun[];
  [key: string]: unknown;
}

/**
 * Snapshot with lines containing glyphs
 */
export interface SnapshotWithLines {
  lines?: SnapshotLine[];
  [key: string]: unknown;
}

/**
 * Typesetter client interface for glyph warming
 */
export interface GlyphWarmingClient {
  warmGlyphPathsBin(fontUid: string, gids: number[], fontSize: number): Promise<{ ok: boolean }>;
}

/**
 * Options for glyph warming
 */
export interface GlyphWarmingOptions {
  /** Font UID for cache key */
  fontUid: string;
  /** Font size in pixels */
  fontSizePx: number;
  /** Maximum glyphs to warm per batch */
  maxGlyphs?: number;
  /** Debug logging function */
  debugLog?: (...args: unknown[]) => void;
  /** Callback to trigger re-render after warming */
  onWarmingComplete?: () => void;
}

/**
 * Result of glyph warming analysis
 */
export interface GlyphWarmingResult {
  /** Total visible glyph IDs found */
  visibleCount: number;
  /** Glyph IDs that need warming (not in cache) */
  uncachedCount: number;
  /** Whether warming request was sent */
  warmingRequested: boolean;
}

// ============================================================================
// GLYPH ID EXTRACTION
// ============================================================================

/**
 * Extracts unique glyph IDs from snapshot lines.
 * 
 * @param snapshot - Snapshot containing lines with runs and glyphs
 * @param maxGlyphs - Maximum glyphs to extract (default: 100)
 * @returns Array of unique glyph IDs
 */
export function extractVisibleGlyphIds(
  snapshot: SnapshotWithLines,
  maxGlyphs: number = 100
): number[] {
  if (!snapshot?.lines) {
    return [];
  }

  const visibleGids = new Set<number>();
  
  for (const line of snapshot.lines) {
    if (line.runs) {
      for (const run of line.runs) {
        if (run.glyphs) {
          for (const glyph of run.glyphs) {
            const gid = glyph.id ?? 0;
            if (gid > 0) {
              visibleGids.add(gid);
            }
          }
        }
      }
    }
  }

  return Array.from(visibleGids).slice(0, maxGlyphs);
}

/**
 * 🆕 2026-01-21: Extracts glyph IDs grouped by font from snapshot lines.
 * 🔧 2026-01-25: Fixed to use font+size composite key to handle same font at different sizes.
 *
 * This is critical for mixed-font content (e.g., bold + regular text).
 * Each font's glyphs must be warmed separately because glyph IDs are
 * font-specific (glyph ID 42 in Inter-Regular is different from glyph ID 42 in Inter-Bold).
 *
 * Additionally, glyph paths are size-dependent - the same glyph at 14px has
 * different path coordinates than the same glyph at 16px. So we must warm
 * glyphs for each font+size combination separately.
 *
 * @param snapshot - Snapshot containing lines with runs and glyphs
 * @param defaultFontUid - Fallback font UID if run doesn't specify one
 * @param defaultFontSizeFp - Fallback font size in 26.6 fixed point
 * @returns Map of compositeKey (fontUid@sizePx) to { fontUid, glyphIds, fontSizePx }
 */
export function extractGlyphsGroupedByFont(
  snapshot: SnapshotWithLines,
  defaultFontUid: string,
  defaultFontSizeFp: number = 14 * 64  // 14px in 26.6 fixed point
): Map<string, { fontUid: string; glyphIds: Set<number>; fontSizePx: number }> {
  const fontSizeGlyphMap = new Map<string, { fontUid: string; glyphIds: Set<number>; fontSizePx: number }>();

  if (!snapshot?.lines) {
    return fontSizeGlyphMap;
  }

  for (const line of snapshot.lines) {
    if (!line.runs) continue;

    for (const run of line.runs) {
      // Extract font UID from run's font_fingerprint
      const fontUid = run.font_fingerprint?.font_uid
                   || run.font_fingerprint?.family
                   || run.font_fingerprint?.postscript_name
                   || defaultFontUid;

      // Convert 26.6 fixed point to pixels
      const fontSizeFp = run.font_size_fp ?? defaultFontSizeFp;
      const fontSizePx = fontSizeFp / 64;

      // 🔧 FIX: Use composite key to handle same font at different sizes
      const compositeKey = `${fontUid}@${fontSizePx}`;

      // Get or create entry for this font+size
      if (!fontSizeGlyphMap.has(compositeKey)) {
        fontSizeGlyphMap.set(compositeKey, { fontUid, glyphIds: new Set(), fontSizePx });
      }

      const entry = fontSizeGlyphMap.get(compositeKey)!;

      // Add glyphs from this run
      if (run.glyphs) {
        for (const glyph of run.glyphs) {
          const gid = glyph.id ?? 0;
          if (gid > 0) {
            entry.glyphIds.add(gid);
          }
        }
      }
    }
  }

  return fontSizeGlyphMap;
}

// ============================================================================
// GLYPH WARMING
// ============================================================================

/**
 * Warms glyph paths for visible content.
 * 
 * Extracts glyph IDs from snapshot, filters out cached ones,
 * and requests paths for uncached glyphs.
 * 
 * @param client - Typesetter client for warming requests
 * @param snapshot - Snapshot containing visible lines
 * @param options - Warming options
 * @returns Result indicating what was warmed
 * 
 * @example
 * ```typescript
 * const result = await warmVisibleGlyphs(typesetterClient, snapshot.snapshot, {
 *   fontUid: resolvedFontUid,
 *   fontSizePx: 14,
 *   debugLog: console.log,
 *   onWarmingComplete: () => renderTextRef.current?.(true, 'glyph-warming')
 * });
 * ```
 */
export async function warmVisibleGlyphs(
  client: GlyphWarmingClient | null | undefined,
  snapshot: SnapshotWithLines | null | undefined,
  options: GlyphWarmingOptions
): Promise<GlyphWarmingResult> {
  const { 
    fontUid, 
    fontSizePx, 
    maxGlyphs = 100,
    debugLog = () => {},
    onWarmingComplete
  } = options;

  const result: GlyphWarmingResult = {
    visibleCount: 0,
    uncachedCount: 0,
    warmingRequested: false
  };

  if (!snapshot?.lines) {
    return result;
  }

  // Extract visible glyph IDs
  const gidsArray = extractVisibleGlyphIds(snapshot, maxGlyphs);
  result.visibleCount = gidsArray.length;

  // Filter out already-cached glyphs to prevent infinite render loop
  const uncachedGids = filterUncachedGlyphs(gidsArray, fontUid, fontSizePx);
  result.uncachedCount = uncachedGids.length;

  debugLog(`🎨 [GlyphWarming] Cache analysis: ${gidsArray.length} visible glyphs, ${uncachedGids.length} need warming, ${gidsArray.length - uncachedGids.length} already cached`);

  // Only warm glyphs that aren't cached yet
  if (!client) {
    debugLog('🎨 [GlyphWarming] ⚠️ Skipping - no client available');
    return result;
  }

  if (uncachedGids.length === 0) {
    debugLog('🎨 [GlyphWarming] ✅ All visible glyphs already cached, skipping request');
    return result;
  }

  // Send warming request
  debugLog(`🎨 [GlyphWarming] 🚀 Sending warmGlyphPathsBin for ${uncachedGids.length} uncached glyphs...`);
  result.warmingRequested = true;

  try {
    const warmResult = await client.warmGlyphPathsBin(fontUid, uncachedGids, fontSizePx);
    debugLog(`🎨 [GlyphWarming] ✅ Warming completed:`, warmResult);

    // Trigger re-render after new glyphs are cached
    if (warmResult?.ok && uncachedGids.length > 0 && onWarmingComplete) {
      debugLog(`🎨 [GlyphWarming] 🔄 Triggering re-render after ${uncachedGids.length} new glyphs cached`);
      requestAnimationFrame(onWarmingComplete);
    }
  } catch (error) {
    console.error(`🎨 [GlyphWarming] ❌ Warming failed:`, error);
  }

  return result;
}

/**
 * Initiates glyph warming without awaiting (fire-and-forget).
 * 
 * Use this when you want to continue rendering immediately
 * while glyph warming happens in background.
 * 
 * @param client - Typesetter client
 * @param snapshot - Snapshot with visible lines
 * @param options - Warming options
 */
export function initiateGlyphWarming(
  client: GlyphWarmingClient | null | undefined,
  snapshot: SnapshotWithLines | null | undefined,
  options: GlyphWarmingOptions
): void {
  // Fire and forget - don't await
  warmVisibleGlyphs(client, snapshot, options).catch(error => {
    console.error(`🎨 [GlyphWarming] Background warming failed:`, error);
  });
}

// ============================================================================
// 🆕 MULTI-FONT GLYPH WARMING (2026-01-21)
// ============================================================================

/**
 * Options for multi-font glyph warming
 */
export interface MultiGlyphWarmingOptions {
  /** Default font UID to use when run doesn't specify one */
  defaultFontUid: string;
  /** Default font size in pixels */
  defaultFontSizePx?: number;
  /** Debug logging function */
  debugLog?: (...args: unknown[]) => void;
  /** Callback to trigger re-render after ALL fonts are warmed */
  onWarmingComplete?: () => void;
}

/**
 * Result of multi-font glyph warming
 */
export interface MultiGlyphWarmingResult {
  /** Number of unique fonts found in snapshot */
  fontCount: number;
  /** Per-font results */
  fontResults: Map<string, GlyphWarmingResult>;
  /** Whether any glyphs were warmed */
  anyWarmed: boolean;
}

/**
 * 🆕 Warms glyph paths for ALL fonts in the snapshot.
 * 
 * This is critical for mixed-font content (e.g., after Ctrl+B applies bold).
 * Unlike warmVisibleGlyphs which only warms a single font, this function:
 * 1. Extracts fontUid from each run's font_fingerprint
 * 2. Groups glyphs by font
 * 3. Warms each font's glyphs in parallel
 * 
 * @param client - Typesetter client for warming requests
 * @param snapshot - Snapshot containing lines with runs and glyphs
 * @param options - Multi-font warming options
 * @returns Result with per-font warming details
 * 
 * @example
 * ```typescript
 * // After applyCharacterFormatting changes fonts in the document
 * const result = await warmVisibleGlyphsMultiFont(typesetterClient, snapshot.snapshot, {
 *   defaultFontUid: 'Inter-Regular.ttf',
 *   defaultFontSizePx: 14,
 *   debugLog: console.log,
 *   onWarmingComplete: () => triggerCanvasRedraw()
 * });
 * ```
 */
export async function warmVisibleGlyphsMultiFont(
  client: GlyphWarmingClient | null | undefined,
  snapshot: SnapshotWithLines | null | undefined,
  options: MultiGlyphWarmingOptions
): Promise<MultiGlyphWarmingResult> {
  const {
    defaultFontUid,
    defaultFontSizePx = 14,
    debugLog = () => {},
    onWarmingComplete
  } = options;
  
  const result: MultiGlyphWarmingResult = {
    fontCount: 0,
    fontResults: new Map(),
    anyWarmed: false
  };
  
  if (!snapshot?.lines) {
    debugLog('🎨 [MultiGlyphWarming] No lines in snapshot, skipping');
    return result;
  }
  
  if (!client) {
    debugLog('🎨 [MultiGlyphWarming] No client available, skipping');
    return result;
  }
  
  // Extract glyphs grouped by font
  const fontGlyphMap = extractGlyphsGroupedByFont(
    snapshot,
    defaultFontUid,
    defaultFontSizePx * 64  // Convert to 26.6 fixed point
  );
  
  result.fontCount = fontGlyphMap.size;
  debugLog(`🎨 [MultiGlyphWarming] Found ${result.fontCount} unique font+size combinations in snapshot:`,
    Array.from(fontGlyphMap.keys()));

  if (result.fontCount === 0) {
    return result;
  }

  // Warm glyphs for each font+size combination in parallel
  const warmingPromises: Promise<void>[] = [];

  // 🔧 FIX (2026-01-25): Use compositeKey and extract fontUid from value
  for (const [compositeKey, { fontUid, glyphIds, fontSizePx }] of fontGlyphMap) {
    const gidsArray = Array.from(glyphIds);

    // Filter out already-cached glyphs
    const uncachedGids = filterUncachedGlyphs(gidsArray, fontUid, fontSizePx);

    const fontResult: GlyphWarmingResult = {
      visibleCount: gidsArray.length,
      uncachedCount: uncachedGids.length,
      warmingRequested: false
    };

    debugLog(`🎨 [MultiGlyphWarming] Font "${fontUid}" @ ${fontSizePx}px: ${gidsArray.length} visible, ${uncachedGids.length} need warming`);

    if (uncachedGids.length > 0) {
      fontResult.warmingRequested = true;
      result.anyWarmed = true;

      // Create warming promise for this font+size
      const warmPromise = client.warmGlyphPathsBin(fontUid, uncachedGids, fontSizePx)
        .then(() => {
          debugLog(`🎨 [MultiGlyphWarming] ✅ Warmed ${uncachedGids.length} glyphs for "${fontUid}" @ ${fontSizePx}px`);
        })
        .catch(error => {
          console.error(`🎨 [MultiGlyphWarming] ❌ Failed to warm "${fontUid}" @ ${fontSizePx}px:`, error);
        });

      warmingPromises.push(warmPromise);
    }

    result.fontResults.set(compositeKey, fontResult);
  }
  
  // Wait for all fonts to be warmed
  if (warmingPromises.length > 0) {
    debugLog(`🎨 [MultiGlyphWarming] Waiting for ${warmingPromises.length} warming requests...`);
    await Promise.all(warmingPromises);
    debugLog(`🎨 [MultiGlyphWarming] ✅ All fonts warmed!`);
    
    // Trigger re-render after all fonts are warmed
    if (onWarmingComplete) {
      debugLog(`🎨 [MultiGlyphWarming] 🔄 Triggering re-render after multi-font warming`);
      requestAnimationFrame(onWarmingComplete);
    }
  }
  
  return result;
}

/**
 * Initiates multi-font glyph warming without awaiting (fire-and-forget).
 * 
 * Use this when you want to continue rendering immediately
 * while glyph warming happens in background.
 * 
 * @param client - Typesetter client
 * @param snapshot - Snapshot with visible lines
 * @param options - Multi-font warming options
 */
export function initiateMultiGlyphWarming(
  client: GlyphWarmingClient | null | undefined,
  snapshot: SnapshotWithLines | null | undefined,
  options: MultiGlyphWarmingOptions
): void {
  // Fire and forget - don't await
  warmVisibleGlyphsMultiFont(client, snapshot, options).catch(error => {
    console.error(`🎨 [MultiGlyphWarming] Background warming failed:`, error);
  });
}
